import type { CelebrityInput } from "@pushup/types";
import { Button } from "@pushup/ui";
import { Loader2 } from "lucide-react";
import {
  useCelebrities,
  useCreateCelebrity,
  useDeleteCelebrity,
  useUpdateCelebrity,
} from "./hooks";
import { CelebrityForm } from "./celebrity-form";
import { CelebrityRow } from "./celebrity-row";

export function CelebrityDashboard() {
  const { data: celebrities, isLoading, isError, refetch } = useCelebrities();
  const createMutation = useCreateCelebrity();
  const updateMutation = useUpdateCelebrity();
  const deleteMutation = useDeleteCelebrity();

  const handleCreate = (input: CelebrityInput) => {
    createMutation.mutate(input);
  };

  const handleUpdate = (id: string) => (input: CelebrityInput) => {
    updateMutation.mutate({ id, input });
  };

  const handleDelete = (id: string) => () => {
    if (window.confirm("Delete this celebrity? This cannot be undone.")) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="space-y-8">
      <section>
        <div className="widget">
          <div className="widget-title">
            <span>ADD CELEBRITY</span>
          </div>
          <div className="widget-body">
            <CelebrityForm
              isSubmitting={createMutation.isPending}
              error={createMutation.error ? createMutation.error.message : null}
              onSave={handleCreate}
            />
          </div>
        </div>
      </section>

      <section>
        <div className="widget">
          <div className="widget-title">
            <span>CELEBRITIES ({celebrities?.length ?? 0})</span>
            <div className="widget-title-tools">
              <button
                type="button"
                className="widget-title-tool"
                aria-label="Refresh"
                onClick={() => void refetch()}
              >
                <svg
                  className="size-3.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                  <path d="M21 3v6h-6" />
                </svg>
              </button>
            </div>
          </div>
          <div className="widget-body">
            {isLoading ? (
              <p className="flex items-center justify-center gap-2 py-8 text-sm text-foreground/60">
                <Loader2 className="size-4 animate-spin" />
                Loading celebrities…
              </p>
            ) : isError ? (
              <div className="space-y-4 py-6 text-center">
                <p className="text-sm text-lb-red">Couldn&apos;t load celebrities.</p>
                <Button variant="outline" onClick={() => void refetch()}>
                  Retry
                </Button>
              </div>
            ) : (celebrities ?? []).length === 0 ? (
              <p className="py-4 text-sm text-foreground/60">
                No celebrities yet. Add your first one above.
              </p>
            ) : (
              <div className="space-y-3">
                {(celebrities ?? []).map((celebrity) => (
                  <CelebrityRow
                    key={celebrity.id}
                    celebrity={celebrity}
                    isUpdating={updateMutation.isPending}
                    isDeleting={
                      deleteMutation.isPending && deleteMutation.variables === celebrity.id
                    }
                    updateError={updateMutation.error ? updateMutation.error.message : null}
                    onUpdate={handleUpdate(celebrity.id)}
                    onDelete={handleDelete(celebrity.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
